// What Mynah knows about one word, wherever the learner found it.
//
// Pulled out of app.ts so the rule can be tested, because it was wrong in a way no test could see:
// `sentences.glossary[text]` on a corpus that has no glossary is a TypeError, thrown inside a tap
// handler, before anything is spoken or drawn. The types said every corpus had one and three of
// the four did not — only build-sentences.mjs writes the field — so the compiler agreed with an
// assumption the data had already broken, and the Japanese course shipped with every tap on a
// non-vocabulary word throwing.

import type { GlossaryEntry, Sentences, Stories, Vocab, Word } from './types.js';

/** What the tap panel needs: how to say it, and what it means. */
export interface WordInfo {
  p: string;
  tones: number[];
  e: string[];
  /** The full vocabulary entry, when the word has a card of its own — the panel shows its part of speech. */
  extra?: Word;
}

/**
 * The graded vocabulary first, then either glossary.
 *
 * The vocabulary wins because it says more and because it keeps ONE answer for what a word means
 * app-wide. A story's concrete nouns — 小猫, 鸟窝, 雪人 — are mostly absent from both the vocabulary
 * and the sentence corpus, so a story carries its own glossary and is asked last.
 *
 * Every glossary is optional here, and must stay optional: a corpus that does not describe its
 * extra words is a corpus with nothing to add, not a reason to stop.
 */
export function lookupWord(
  vocab: Vocab,
  sentences: Pick<Sentences, 'glossary'>,
  stories: Pick<Stories, 'glossary'>,
  text: string,
): WordInfo | null {
  const word = vocab.words.find((w) => w.s === text);
  if (word) return { p: word.p, tones: word.tones, e: word.e, extra: word };
  const entry: GlossaryEntry | undefined =
    sentences.glossary?.[text] ?? stories.glossary?.[text];
  return entry ? { p: entry.p, tones: entry.tones, e: entry.e } : null;
}
